import { Routes } from '@angular/router';
import { NotFound } from './shared/components/not-found/not-found';
import { LandingPage } from './modules/landing-page/landing-page';
import { afterLoginGuard } from './middleware/guards/after-login-guard';
import { beforeLoginGuard } from './middleware/guards/before-login.guard';
import { moduleSelectionGuard } from './middleware/guards/module-selection.guard';
import { NotAllowed } from './shared/components/not-allowed/not-allowed';
import { SpModuleView } from './modules/strategic-planning/sp-module-view/sp-module-view';
import { strategicPlanmodulesRoutes } from './modules/strategic-planning/sp-module-view/sp-module.routing';
import { ConfigsOutlet } from './modules/system-configs/configs-outlet/configs-outlet';
import { systemConfigurationsRoutes } from './modules/system-configs/system-configs.routes';
import { systemUtilitiesRoutes } from './modules/system-utilities/utilities.routing';
import { UtilitiesOutlet } from './modules/system-utilities/utilities-outlet/utilities-outlet';
import { superAdminUtilitiesRoutes } from './modules/system-utilities/super-admin-utilities.routing';
import { SuperAdminUtilitiesOutlet } from './modules/system-utilities/super-admin-utilities-outlet/super-admin-utilities-outlet';
import { sectorModuleRoutes } from './modules/me-sector/sector-module-view/sector-module-view.routing';
import { SectorModuleView } from './modules/me-sector/sector-module-view/sector-module-view';
import { ProjectModuleView } from './modules/project-module/project-module-view/project-module-view';
import { projectModuleRoutes } from './modules/project-module/project-module.routing';
import { NationalModuleView } from './modules/national/national-module-view/national-module-view';
import { nationalModuleRoutes } from './modules/national/national-module-view/national-module-view.routing';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'home',
  },
  {
    path: 'home',
    pathMatch: 'full',
    component: LandingPage,
    canActivate: [beforeLoginGuard],
  },
  {
    path: 'projects',
    loadComponent: () =>
      import('./modules/landing-page/projects-view/projects-view').then((m) => m.ProjectsView),
    canActivate: [beforeLoginGuard],
  },
  {
    path: 'documents',
    loadComponent: () =>
      import('./modules/landing-page/more-documents-view/more-documents-view').then(
        (m) => m.MoreDocumentsView,
      ),
    canActivate: [beforeLoginGuard],
  },
  {
    path: 'learn-more',
    loadComponent: () =>
      import('./modules/landing-page/learn-more-view/learn-more-view').then((m) => m.LearnMoreView),
    canActivate: [beforeLoginGuard],
  },
  {
    path: 'faqs',
    loadComponent: () =>
      import('./modules/landing-page/more-faqs/more-faqs').then((m) => m.MoreFaqs),
    canActivate: [beforeLoginGuard],
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./modules/landing-page/login-page/login-page').then((m) => m.LoginPage),
    canActivate: [beforeLoginGuard],
  },
  {
    path: 'profile',
    loadComponent: () =>
      import('./modules/user-profile/profile-view/profile-view').then((m) => m.ProfileView),
    canActivate: [afterLoginGuard],
  },
  {
    path: 'notifications',
    loadComponent: () =>
      import('./modules/user-profile/notifications-view/notifications-view').then((m) => m.NotificationsView),
    canActivate: [afterLoginGuard],
  },
  {
    path: 'change-password',
    loadComponent: () =>
      import('./modules/user-profile/change-password-view/change-password-view').then(
        (m) => m.ChangePasswordView,
      ),
    canActivate: [afterLoginGuard],
  },
  {
    path: 'set-password',
    loadComponent: () =>
      import('./modules/user-profile/set-password/set-password').then(
        (m) => m.SetPassword,
      ),
  },
  {
    path: 'forgot-password',
    loadComponent: () =>
      import('./modules/user-profile/forgot-password/forgot-password').then(
        (m) => m.ForgotPassword,
      ),
  },
  {
    path: '',
    canActivate: [afterLoginGuard],
    children: [
      {
        path: 'module-selection',
        canActivate: [moduleSelectionGuard],
        loadComponent: () =>
          import('./modules/landing/module-selection/module-selection').then((m) => m.ModuleSelection),
      },
      {
        path: 'modules',
        loadComponent: () =>
          import('./modules/landing/landing-modules/landing-modules').then((m) => m.LandingModules),
      },
      {
        path: 'frameworks',
        loadComponent: () =>
          import('./modules/framework-configuration/framework-configuration').then(
            (m) => m.FrameworkConfiguration,
          ),
      },
      {
        path: 'frameworks/:id',
        loadComponent: () =>
          import('./modules/framework-configuration/viewed-framework/viewed-framework').then(
            (m) => m.ViewedFramework,
          ),
      },
      {
        path: 'frameworks/:id/nodes/:nodeId',
        loadComponent: () =>
          import('./modules/framework-configuration/view-node/view-node').then(
            (m) => m.ViewNodePage,
          ),
      },
      {
        path: 'mne',
        children: [
          {
            path: 'frameworks',
            data: { name: 'M&E Frameworks' },
            loadComponent: () =>
              import('./modules/strategic-planning/mne-frameworks/mne-frameworks').then(
                (m) => m.MneFrameworks,
              ),
          },
          {
            path: 'strategic-plans',
            data: { name: 'Strategic Plans' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plans-list/strategic-plans-list').then(
                    (m) => m.StrategicPlansList,
                  ),
              },
              {
                path: ':spId',
                component: SpModuleView,
                children: [...strategicPlanmodulesRoutes],
              },
            ],
          },
          {
            path: 'projects-mel',
            data: { name: 'Projects' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./modules/project-module/projects-list/projects-list').then(
                    (m) => m.ProjectsList,
                  ),
              },
              {
                path: 'dashboard',
                loadComponent: () =>
                  import('./modules/project-module/sections/dashboard/project-dashboard').then(
                    (m) => m.ProjectDashboard,
                  ),
              },
              {
                path: ':projectId',
                component: ProjectModuleView,
                children: [...projectModuleRoutes],
              },
            ],
          },
          {
            path: 'national',
            data: { name: 'National' },
            component: NationalModuleView,
            children: [...nationalModuleRoutes],
          },
          {
            path: 'mne-sector',
            data: { name: 'Posh' },
            component: SectorModuleView,
            children: [
              ...sectorModuleRoutes
            ],
          },
          {
            path: 'annual-plans',
            component: SpModuleView,
            data: { name: 'MTEF Annual Plans' },
            children: [
              {
                path: '',
                redirectTo: 'plan',
                pathMatch: 'full',
              },
              {
                path: 'plan',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/sp-annual-plans').then(
                    (m) => m.SpAnnualPlans,
                  ),
              },
              {
                path: 'planned-reviews',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/planned-reviews/sp-planned-reviews').then(
                    (m) => m.SpPlannedReviews,
                  ),
              },
              {
                path: 'planned-reviews/:id',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/planned-reviews/planned-review-details/planned-review-details').then(
                    (m) => m.PlannedReviewDetails,
                  ),
              },
              {
                path: 'plan-details/:id',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/plan-details/plan-details/plan-details').then(
                    (m) => m.PlanDetails,
                  ),
              },
              {
                path: 'plan-details/:id/activity/:activityId',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/plan-details/view-activity/view-activity.component').then(
                    (m) => m.ViewActivityComponent,
                  ),
              },
              {
                path: 'plan-details/:id/activity/:activityId/task/:taskId',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/plan-details/view-task/view-task.component').then(
                    (m) => m.ViewTaskComponent,
                  ),
              },
            ],
          },
          {
            path: 'annual-plans/plan-details/:id',
            component: SpModuleView,
            data: { name: 'Plan Details' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/plan-details/plan-details/plan-details').then(
                    (m) => m.PlanDetails,
                  ),
              },
            ],
          },
          {
            path: 'annual-plans/plan-details/:id/activity/:activityId',
            component: SpModuleView,
            data: { name: 'Activity Details' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/plan-details/view-activity/view-activity.component').then(
                    (m) => m.ViewActivityComponent,
                  ),
              },
            ],
          },
          {
            path: 'annual-plans/plan-details/:id/activity/:activityId/task/:taskId',
            component: SpModuleView,
            data: { name: 'Task Details' },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./modules/strategic-planning/strategic-plan-features/sp-annual-plans/plan-details/view-task/view-task.component').then(
                    (m) => m.ViewTaskComponent,
                  ),
              },
            ],
          },
          {
            path: 'system-configs',
            component: ConfigsOutlet,
            children: [...systemConfigurationsRoutes],
          },
          {
            path: 'institution',
            component: UtilitiesOutlet,
            data: { name: 'Institution Settings' },
            children: [...systemUtilitiesRoutes],
          },
          {
            path: 'system-utilities',
            component: SuperAdminUtilitiesOutlet,
            data: { name: 'System Utilities' },
            children: [...superAdminUtilitiesRoutes],
          },
          {
            path: 'custom-mne',
            data: { name: 'My Custom M&E' },
            loadComponent: () =>
              import('./modules/custom-modules/custom-mne-list/custom-mne-list').then(
                (m) => m.CustomMneList,
              ),
          },
          {
            path: 'programmes/:id',
            data: { name: 'Programme Details' },
            loadComponent: () =>
              import('./modules/custom-modules/view-programme/view-programme').then(
                (m) => m.ViewProgramme,
              ),
          },
          {
            path: 'programmes/:id/nodes/:nodeId',
            data: { name: 'Programme Node Details' },
            loadComponent: () =>
              import('./modules/custom-modules/programme-node-details/programme-node-details').then(
                (m) => m.ProgrammeNodeDetails,
              ),
          },
        ],
      },
    ],
  },
  {
    path: 'not-allowed',
    component: NotAllowed,
  },
  {
    path: '**',
    component: NotFound,
  },
];

